import api from './api';

export interface InstallmentOption {
  installment: number;
  has_interest: boolean;
  value: number;
  currency: string;
  interest_percentage: number;
}

export interface InstallmentsResponse {
  success: boolean;
  brand: string;
  total: number;
  installments: InstallmentOption[];
  simulated?: boolean;
}

export interface CreditCardPaymentData {
  amount: number;
  payment_token: string;
  installments: number;
  cpf: string;
  birth_date?: string;
  billing_address: {
    street: string;
    number: string;
    neighborhood: string;
    zipcode: string;
    city: string;
    state: string;
  };
}

export interface CreditCardPaymentResponse {
  success: boolean;
  payment_id: string;
  charge_id: string;
  status: string;
  amount: number;
  installments: number;
  message: string;
}

export interface SubscriptionPlanData {
  name: string;
  amount: number;
  interval?: 'monthly' | 'yearly';
  description?: string;
}

export interface SubscriptionPlanResponse {
  success: boolean;
  plan_id: string;
  name: string;
  amount: number;
  interval: string;
  simulated?: boolean;
  message: string;
}

export interface SubscriptionData {
  plan_id: string;
  payment_token: string;
  cpf: string;
  birth_date?: string;
  billing_address: {
    street: string;
    number: string;
    neighborhood: string;
    zipcode: string;
    city: string;
    state: string;
  };
}

export interface SubscriptionResponse {
  success: boolean;
  subscription_id: string;
  plan_id: string;
  status: string;
  next_execution: string;
  simulated?: boolean;
  message: string;
}

class EfiPayService {
  /**
   * Consultar parcelas disponíveis para uma bandeira de cartão
   */
  async getInstallments(brand: string, total: number): Promise<InstallmentsResponse> {
    try {
      const response = await api.get('/payments/efipay/installments', {
        params: { brand, total }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Erro ao consultar parcelas:', error);
      throw new Error(error.response?.data?.detail || 'Erro ao consultar parcelas');
    }
  }
  
  /**
   * Criar pagamento com cartão de crédito
   */
  async createCreditCardPayment(paymentData: CreditCardPaymentData): Promise<CreditCardPaymentResponse> {
    try {
      console.log('💳 Criando pagamento com cartão:', {
        amount: paymentData.amount,
        installments: paymentData.installments,
        payment_token: paymentData.payment_token.substring(0, 10) + '...'
      });
      
      const response = await api.post('/payments/efipay/credit-card', paymentData);
      
      console.log('✅ Pagamento criado:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao criar pagamento:', error);
      throw new Error(error.response?.data?.detail || 'Erro ao processar pagamento');
    }
  }
  
  /**
   * Criar plano de assinatura
   */
  async createSubscriptionPlan(planData: SubscriptionPlanData): Promise<SubscriptionPlanResponse> {
    try {
      console.log('📋 Criando plano de assinatura:', planData);
      
      const response = await api.post('/payments/efipay/subscription/plan', planData);
      
      console.log('✅ Plano criado:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao criar plano:', error);
      throw new Error(error.response?.data?.detail || 'Erro ao criar plano de assinatura');
    }
  }
  
  /**
   * Criar assinatura recorrente
   */
  async createSubscription(subscriptionData: SubscriptionData): Promise<SubscriptionResponse> {
    try {
      console.log('🔄 Criando assinatura:', {
        plan_id: subscriptionData.plan_id,
        payment_token: subscriptionData.payment_token.substring(0, 10) + '...'
      });
      
      const response = await api.post('/payments/efipay/subscription', subscriptionData);
      
      console.log('✅ Assinatura criada:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao criar assinatura:', error);
      throw new Error(error.response?.data?.detail || 'Erro ao criar assinatura');
    }
  }
  
  /**
   * Cancelar assinatura
   */
  async cancelSubscription(subscriptionId: string): Promise<{ success: boolean; message: string }> {
    try {
      console.log('🛑 Cancelando assinatura:', subscriptionId);
      
      const response = await api.delete(`/payments/efipay/subscription/${subscriptionId}`);
      
      console.log('✅ Assinatura cancelada:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erro ao cancelar assinatura:', error);
      throw new Error(error.response?.data?.detail || 'Erro ao cancelar assinatura');
    }
  }
  
  /**
   * Planos pré-definidos da Advogada Parceira
   */
  getDefaultPlans() {
    return [
      {
        id: 'monthly',
        name: 'Plano Mensal',
        amount: 29.90,
        interval: 'monthly' as const,
        description: 'Assinatura mensal com renovação automática a cada 30 dias',
        features: [
          'Acesso completo à plataforma',
          'Suporte via chat',
          'Atualizações automáticas'
        ]
      },
      {
        id: 'yearly',
        name: 'Plano Anual',
        amount: 299.90,
        interval: 'yearly' as const,
        description: 'Assinatura anual com desconto. Pode ser parcelada em até 6x sem juros ou 12x com juros',
        features: [
          'Acesso completo à plataforma',
          'Suporte prioritário',
          'Atualizações automáticas',
          '2 meses grátis (economia de R$ 59,80)'
        ],
        discount: '16% de desconto'
      }
    ];
  }
}

// Instância singleton
const efiPayService = new EfiPayService();
export default efiPayService;